import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import DocumentUploader from "./DocumentUploader";
import InsuranceCard from "./InsuranceCard";
import VehicleCard from "./VehicleCard";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CLS = { none: "bg-slate-700/40 text-slate-300 border-slate-600", pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  verified: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", clear: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/20 text-red-300 border-red-500/30", failed: "bg-red-500/20 text-red-300 border-red-500/30" };

export default function DriverVerificationCard({ onStatus }) {
  const [st, setSt] = useState(null);
  const [lic, setLic] = useState(null);
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const load = () => api.get("/gigs/driver/status").then((r) => { setSt(r.data); onStatus?.(r.data); });
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!st) return null;

  const apply = async () => {
    setBusy(true);
    try { await api.post("/gigs/driver/apply", { license_doc_path: lic, full_name: name, consent_background_check: consent, home_city: city, phone, notify_new_jobs: true, referral_code: refCode }); toast.success("Application submitted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };
  const savePrefs = async (patch) => {
    try { const { data } = await api.post("/gigs/driver/prefs", patch); setSt(data); toast.success("Preferences saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  return (
    <div className="p-5 rounded-2xl bg-[#131722] border border-slate-800 space-y-3" data-testid="driver-verification-card">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" />Driver eligibility</p>
        <div className="flex gap-1.5">
          <Badge className={`border text-[10px] ${CLS[st.status] || CLS.none}`} data-testid="dv-license-badge">License: {st.status}</Badge>
          <Badge className={`border text-[10px] ${CLS[st.background_check] || CLS.none}`} data-testid="dv-bgc-badge">Background: {st.background_check}</Badge>
        </div>
      </div>
      {st.status === "verified" && st.background_check === "clear" ? (
        <div className="space-y-2" data-testid="dv-eligible">
          <p className={`text-sm ${st.eligible ? "text-emerald-300" : "text-amber-300"}`}>{st.eligible ? "You're cleared to claim delivery jobs." : "License & background cleared — add valid insurance to start claiming."}</p>
          <InsuranceCard st={st} onSaved={() => load()} />
          <VehicleCard vehicle={st.vehicle} onSaved={() => load()} />
          <div className="flex gap-2">
            <Input defaultValue={st.home_city || ""} placeholder="Home city for alerts" data-testid="dv-prefs-city" className="bg-slate-900 border-slate-800 h-8 text-xs"
              onBlur={(e) => e.target.value.trim() !== (st.home_city || "") && savePrefs({ home_city: e.target.value })} />
            <Input defaultValue={st.phone || ""} placeholder="+1 SMS (optional)" data-testid="dv-prefs-phone" className="bg-slate-900 border-slate-800 h-8 text-xs"
              onBlur={(e) => e.target.value.trim() !== (st.phone || "") && savePrefs({ phone: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <Checkbox checked={st.notify_new_jobs !== false} onCheckedChange={(v) => savePrefs({ notify_new_jobs: !!v })} data-testid="dv-prefs-notify" />
            Alert me when a new job is posted in my city
          </label>
        </div>
      )
      : st.status === "pending" ? <p className="text-sm text-slate-400" data-testid="dv-pending">Your application is under review. We'll email you once your license and background check are cleared.</p>
      : (
        <div className="space-y-3" data-testid="dv-apply-form">
          <p className="text-sm text-slate-400">Upload your driver's license and consent to a background check to start claiming paid delivery runs.</p>
          {refCode && <p className="text-xs text-pink-300" data-testid="dv-referral-applied">Referral code <b className="font-mono">{refCode}</b> applied — your friend earns a bonus when you complete your first run.</p>}
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full legal name (as on license)" data-testid="dv-name-input" className="bg-slate-900 border-slate-800" />
          <div className="grid grid-cols-2 gap-2">
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Home city (job alerts)" data-testid="dv-city-input" className="bg-slate-900 border-slate-800" />
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 mobile (SMS alerts)" data-testid="dv-phone-input" className="bg-slate-900 border-slate-800" />
          </div>
          <DocumentUploader kind="license" label="Driver's license" hint="Front side, JPG/PNG/PDF" value={lic} onChange={setLic} />
          <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="dv-consent-checkbox" className="mt-0.5" />
            I consent to a motor-vehicle and criminal background check as required to deliver on MY VELOCITY.
          </label>
          <Button onClick={apply} disabled={busy || !lic || name.length < 2 || !consent} data-testid="dv-apply-btn" className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply to drive"}
          </Button>
        </div>
      )}
    </div>
  );
}
